import { Badge, Box } from '@chakra-ui/react';
import {
  ButtonGroup,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  capitalize,
} from '@mui/material';
import Card from 'components/card';
import GeoRestrictionAlert from 'components/GeoRestrictionAlert';
import MButton from 'components/Mbutton';
import MenuDrop from 'components/MenuDrop';
import MuiAUtocomplete from 'components/MuiAutocomplete';
import MuiDatePicker from 'components/MuiDatePicker';
import MuiSelect from 'components/MuiSelect';
import MuiTable from 'components/MuiTable';
import PaginateBox from 'components/PaginateBox';
import SearchModal from 'components/SearchModal';
import { FromArchiveIcon, FromTheFieldIcon } from 'components/TableIcon';
import { CHECK_PERMISSIONS, getEmployerHeader } from 'Constaints';
import dayjs from 'dayjs';
import useDataFetching from 'hooks/useDataFetching';
import useAppQuery from 'hooks/useAppQuery';
import React, { useEffect, useMemo, useState } from 'react';
import { useAuthUser } from 'react-auth-kit';
import { useTranslation } from 'react-i18next';
import {
  BsMinecart,
  BsPersonFill,
  BsPlusCircle,
  BsThreeDotsVertical,
  BsXCircle,
} from 'react-icons/bs';
import { useLocation } from 'react-router-dom';
import {
  checkIsValidDate,
  checkIsValidPermit,
  formatDate,
  formatNumber,
  getExpiredPermitDate,
} from 'utils';
import { config } from 'variables/config';
import PrintButtonExcel from '../default/components/PrintButtonExcel';
import AddEmployer from './AddEmployer';
import MuiAsyncAutocomplete from 'components/MuiAsyncAutocomplete';
import EmployesByEnterpriseModal from './EmployesByEnterpriseModal';
import MModal from './modals/EmployeeModalInfos';
import CheckAuthorization from 'components/security/CheckAutorization';
import { ROLES_LIST } from 'Constaints';
import CheckPrivileges from 'components/CheckPrivileges';
import { getEmployerHeaderWithHistory } from 'Constaints';
import toast from 'react-hot-toast';

const EmployeList = () => {
  const auth = useAuthUser();
  const { t } = useTranslation('employer_list');

  const [searchTerm, setSearchTerm] = useState(null);
  const [selectedEmployer, setSelectedEmployer] = useState(null);
  const location = useLocation();

  const [creator, setCreator] = useState(null);

  const defaultText = t('no_work_permit');
  const [noDataFoundText, setNoDataFoundText] = useState(defaultText);

  const [params, setParams] = useState({
    enterpriseId: location.state?.enterpriseId,
    start: location.state?.start,
    end: location.state?.end,
    startUpdate: location.state?.startUpdate,
    endUpdate: location.state?.endUpdate,
    visaExpired: location.state?.visaExpired,
    expiredIn: location.state?.expiredIn,

    regionId: location.state?.regionId,
    departmentId: location.state?.departmentId,
    arrondissementId: location.state?.arrondissementId,
    page: 0,
    limit: 50,
    fromArchive: location.state?.fromArchive,
    creatorId: location?.state?.creator?.id,
  });

  useEffect(() => {
    if (location.state) {
      setCreator(location?.state?.creator);
      setParams({
        enterpriseId: location.state?.enterpriseId,
        start: location.state?.start,
        end: location.state?.end,
        startUpdate: location.state?.startUpdate,
        endUpdate: location.state?.endUpdate,
        visaExpired: location.state?.visaExpired,
        expiredIn: location.state?.expiredIn,
        regionId: location.state?.regionId,
        departmentId: location.state?.departmentId,
        arrondissementId: location.state?.arrondissementId,
        page: 0,
        limit: 50,
        fromArchive: location.state?.fromArchive,
        creatorId: location?.state?.creator?.id,
      });
    }
  }, [location]);

  /* Fetching enterprises labels — cached 5 min, survives navigation */
  const { data: enterprises } = useAppQuery(`${config.app.api_url}/enterprises/labels`);

  const [loadingSearch, , searchEmployes, fetchDataSearch] = useDataFetching(
    searchTerm?.length > 2 ? `${config.app.api_url}/employers/search/${searchTerm}` : null
  );

  /* Current user — cached 5 min, rarely changes */
  const { data: user } = useAppQuery(`${config.app.api_url}/users/${auth()?.user?.id}`);

  /* Regions — static reference data, cached 5 min */
  const { data: regions } = useAppQuery(`${config.app.api_url}/regions`);
  const [region, setRegion] = useState('*');

  const [departement, setDepartement] = useState(null);
  /* Departments by region — cached per region id */
  const { data: departements } = useAppQuery(
    region && region !== '*' ? `${config.app.api_url}/regions/${region}/departments` : null
  );

  /* Arrondissements by department — cached per department id */
  const { data: arrondisements } = useAppQuery(
    departement ? `${config.app.api_url}/departments/${departement}/arrondissements` : null
  );

  // Build a stable query string from params — becomes part of the React Query cache key.
  // Same params on re-navigation → cache hit → NO network call.
  const employeesQueryString = useMemo(() => {
    return Object.keys(params)
      .filter((key) => params[key] != null && params[key] !== '')
      .map((key) => `${key}=${params[key]}`)
      .join('&');
  }, [params]);

  const {
    isLoading: loading,
    data: employeesRaw,
    error: employeesError,
    refetch: getEmployees,
  } = useAppQuery(
    `${config.app.api_url}/employers/query-names-only?${employeesQueryString}`,
    { staleTime: 30_000 } // paginated filtered list — refresh after 30 s
  );

  // The API returns { data: { content, totalElements, totalPages } }
  // useAppQuery unwraps response.data, so we get { data: { content, ... } }
  const employees = employeesRaw?.data;

  // Handle geo-restriction and reset the empty-text message
  useEffect(() => {
    setNoDataFoundText(defaultText);
    if (!employeesError) return;

    if (
      employeesError?.response?.status === 403 &&
      employeesError?.response?.data?.isGeoRestrictionFlagged
    ) {
      const errorData = employeesError.response.data;
      const scopeType = errorData?.scopeType || 'périmètre géographique';
      const allowedIds = errorData?.allowedIds || '[]';
      const requestedIds = errorData?.requestedIds || '[]';

      setNoDataFoundText(
        <GeoRestrictionAlert
          scopeType={scopeType}
          allowedIds={allowedIds}
          requestedIds={requestedIds}
        />
      );

      toast.error(
        <div className="flex flex-col gap-2">
          <strong>{t('error_geo_restriction_title') || 'Restriction Géographique'}</strong>
          <p>
            {t('error_geo_restriction_message') ||
              `Vous avez tenté d'accéder à ${scopeType} en dehors de votre portée autorisée.`}
          </p>
          <div className="text-xs">
            <p>
              <strong>{t('allowed') || 'Autorisé'}:</strong> {allowedIds}
            </p>
            <p>
              <strong>{t('requested') || 'Demandé'}:</strong> {requestedIds}
            </p>
          </div>
        </div>,
        { duration: 6000, className: 'bg-red-600 text-white' }
      );
    }
  }, [employeesError]);

  const [open, setOpen] = useState(false);
  const [selectedEm, setSetselectedEm] = useState('');

  const handleClick = (item) => {
    if (item) {
      setSelectedEmployer(item);
      setOpenAdd(true);
    }
  };

  // En-têtes pour l'exportation Excel, traduits
  const headerTableExport = [
    '#',
    t('full_name'),
    t('sex'),
    t('company'),
    t('position'),
    t('nationality'),
    t('permit_number'),
    t('permit_expiration_date'),
    t('permit_status'),
  ];

  const [openAdd, setOpenAdd] = useState(false);
  // La création passe obligatoirement par la liste des employés d'une entreprise : on demande
  // d'abord l'entreprise, puis on ouvre sa liste, d'où l'employé sera créé.
  const [openEnterprisePicker, setOpenEnterprisePicker] = useState(false);
  const [pickedEnterprise, setPickedEnterprise] = useState(null);
  const [openEmployesByEnterprise, setOpenEmployesByEnterprise] = useState(false);

  const handleRestFilter = () => {
    setParams({
      start: '',
      end: '',
      enterpriseId: '',
      visaExpired: '',
      arrondissementId: '',
      departmentId: '',
      fromArchive: '',
      regionId: '',
    });
    setRegion('National');
    setDepartement('');
  };
  const exportPdf = (
    <CheckPrivileges roles={['ADMIN', 'QUALITY']}>
      <PrintButtonExcel
        text={t('export')}
        filename={`expatriés_${dayjs(new Date())?.format('DD/MM/YYYY')}`}
        header={headerTableExport}
        body={
          employees?.content?.length > 0 &&
          employees?.content?.map((e, index) => {
            return [
              index + 1,
              e?.fullName || e?.firstName + ' ' + e?.lastName,
              e?.sexe,
              e?.enterpriseName,
              e?.occupation,
              e?.nationality,
              e?.permitNumber,
              e?.permitDate
                ? dayjs(getExpiredPermitDate(e?.permitDate)).format('DD-MM-YYYY')
                : '--',
              !checkIsValidPermit(e) ? t('expired') : t('valid'), // Traduit
            ];
          })
        }
      />
    </CheckPrivileges>
  );
  const menu = {
    icon: <BsThreeDotsVertical />,
    label: t('options'), // Traduit
    Items: [
      {
        label: t('add_expatriate'), // Traduit
        icon: <BsPlusCircle />,
        handleClick: () => setOpenEnterprisePicker(true),
      },
      {
        label: '',
        icon: exportPdf,
      },
      {
        label: t('reset_filter'), // Traduit
        icon: <BsXCircle />,
        handleClick: handleRestFilter,
      },
    ],
  };

  function createData(N, Noms, Poste, entreprise, Nationalité, Date, Status, Options) {
    return {
      N,
      Noms,
      Poste,
      entreprise,
      Nationalité,
      Date,
      Status,
      Options,
    };
  }
  const rows = !employees?.content
    ? []
    : employees?.content?.map((item, index) => {
        return {
          handleClick: () => handleClick(item),
          datas: createData(
            params?.page * params?.limit + (index + 1),
            <>
              {item?.fromArchive ? <FromArchiveIcon /> : <FromTheFieldIcon />}
              {
                <span className="fs-c">
                  {item?.fullName || item?.firstName + ' ' + item?.lastName}
                </span>
              }
            </>,
            item.occupation,
            item?.enterpriseName,
            // formatNumber(item?.salary),
            item?.nationality,
            item?.permitDate
              ? dayjs(getExpiredPermitDate(item?.permitDate)).format('DD-MM-YYYY')
              : '--',
            !item?.permitNumber && !item?.permitDate ? (
              <Chip
                color="error"
                variant="outlined"
                className="capitalize-none"
                label={capitalize(t('no_permit'))}
              /> // Traduit
            ) : !checkIsValidDate(item?.permitDate) ? (
              <Chip color="error" className="capitalize-none" label={capitalize(t('expired'))} /> // Traduit
            ) : item?.permitNumber ? (
              <Chip
                className="capitalize-none"
                variant="outlined"
                color="success"
                label={t('valid')}
              /> // Traduit
            ) : (
              <Chip
                className="capitalize-none"
                variant="outlined"
                color="warning"
                label={t('to_be_checked')}
              />
            ), // Traduit
            <MButton
              variant={'white'}
              style={{ borderRadius: 20 }}
              onClick={() => handleClick(item)}
            >
              {t('consult')} {/* Traduit */}
            </MButton>
          ),
          history: item?.contracts
            ? {
                title: t('contract_history'), // Traduit
                header: [
                  t('salary_fafc'), // Traduit
                  t('allowances'), // Traduit
                  t('permit_number'), // Traduit
                  t('permit_obtention_date'), // Traduit
                  t('permit_expiration_date'), // Traduit
                ],
                datas: item?.contracts
                  ?.sort((a, b) => new Date(b?.permitDate) - new Date(a?.permitDate))
                  ?.map((c, index) => {
                    return {
                      index: index,
                      // salary: formatNumber(c?.salary),
                      idenmites:
                        (c?.indLogement || 0) +
                        (c?.indTransport || 0) +
                        (c?.indResponsabilite || 0) +
                        (c?.indRepresentation || 0) +
                        (c?.primeExploitation || 0) +
                        (c?.sursalaire || 0) +
                        (c?.primeSalissure || 0) +
                        (c?.indCaisse || 0) +
                        (c?.bonus || 0) +
                        (c?.primePanier || 0) +
                        (c?.billetAvion || 0) +
                        (c?.autresPrimes || 0) +
                        (c?.sursalary || 0),
                      permitOptDate: c?.permitDate ? formatDate(c?.permitDate) : '--',
                      permitDate: c?.permitDate
                        ? formatDate(getExpiredPermitDate(c?.permitDate))
                        : '--',
                      permitNumber: c?.permitNumber || '--',
                    };
                  }),
              }
            : undefined,
        };
      });

  return (
    <div>
      <MModal open={open} setOpen={setOpen} item={selectedEm} />
      <div className=" grid h-full grid-cols-1 gap-5">
        {user?.data?.roles.length > 0 &&
          user?.data?.roles?.some((role) =>
            CHECK_PERMISSIONS(role?.permissions, 'Filtres et Affichages')
          ) && (
            <Box
              bg={'rgba(255, 255, 255, 1)'}
              boxShadow="lg"
              p={2}
              rounded={'md'}
              border="1px"
              borderColor="gray.200"
              className=" flex flex-wrap gap-1  items-end justify-end"
            >
              <MuiDatePicker
                label={t('start_period')}
                value={params?.start}
                onChange={(newValue) => {
                  setParams({
                    ...params,
                    start: new Date(newValue)?.getTime(),
                  });
                }}
                wrapperClasses={'flex-1 filter-item anime slideLeft'}
                dataCy="filter-start-period"
              />

              <MuiDatePicker
                value={params?.end}
                label={t('end_period')}
                onChange={(newValue) => {
                  setParams({
                    ...params,
                    end: new Date(newValue)?.getTime(),
                  });
                }}
                wrapperClasses={'flex-1 filter-item anime slideLeft'}
                dataCy="filter-end-period"
              />
              <MuiDatePicker
                label={t('start_modification')}
                value={params?.startUpdate}
                onChange={(newValue) => {
                  setParams({
                    ...params,
                    startUpdate: new Date(newValue)?.getTime(),
                  });
                }}
                wrapperClasses={'flex-1 filter-item anime slideLeft'}
                dataCy="filter-start-update"
              />
              <MuiDatePicker
                value={params?.endUpdate}
                label={t('end_modification')}
                onChange={(newValue) => {
                  setParams({
                    ...params,
                    endUpdate: new Date(newValue)?.getTime(),
                  });
                }}
                wrapperClasses={'flex-1 filter-item anime slideLeft'}
                dataCy="filter-end-update"
              />

              <MuiSelect
                label={t('region')}
                options={regions?.data}
                emptyLabel={t('national')}
                value={params?.regionId}
                defaultFormat={true}
                onChange={(e) => {
                  setRegion(e.target.value);
                  setDepartement('');
                  setParams({
                    ...params,
                    regionId: e.target.value,
                    departmentId: null,
                    arrondissementId: null,
                  });
                }}
                wrapperClasses={'flex-1 filter-item anime slideLeft'}
                dataCy="filter-region"
              />

              {/* Filtre departement */}
              {departements?.data && (
                <MuiSelect
                  label={t('department')}
                  options={departements?.data}
                  emptyLabel={t('regional')}
                  value={params?.departmentId}
                  defaultFormat={true}
                  onChange={(e) => {
                    setDepartement(e.target.value);
                    setParams({
                      ...params,
                      departmentId: e.target.value,
                      arrondissementId: null,
                    });
                  }}
                  wrapperClasses={'flex-1 filter-item anime slideDown'}
                  dataCy="filter-department"
                />
              )}
              {arrondisements?.data && (
                <MuiSelect
                  label={t('arrondissement')}
                  emptyLabel={t('departmental')}
                  options={arrondisements?.data}
                  value={params?.arrondissementId}
                  defaultFormat={true}
                  onChange={(e) => {
                    setParams({
                      ...params,
                      arrondissementId: e.target.value,
                    });
                  }}
                  wrapperClasses={'flex-1 filter-item anime slideDown'}
                  dataCy="filter-arrondissement"
                />
              )}
              <MuiAUtocomplete
                label={t('enterprise')}
                options={enterprises?.data}
                format={{ value: 'id', label: 'socialRaison' }}
                value={params?.enterpriseId}
                onChange={(e, item) => {
                  setParams({
                    ...params,
                    enterpriseId: item?.value,
                  });
                }}
                wrapperClasses={'flex-1 filter-item anime slideRight'}
                dataCy={'enterprise-autocomplete'}
              />
              <MuiSelect
                label={t('permit_expired')}
                emptyLabel={t('all')}
                value={params?.visaExpired}
                options={[
                  { value: false, label: t('no') }, // Traduit
                  { value: true, label: t('yes') }, // Traduit
                ]}
                onChange={(e) => {
                  setParams({
                    ...params,
                    visaExpired: e.target.value?.toString(),
                  });
                }}
                wrapperClasses={'flex-1 filter-item anime slideUp'}
                dataCy="filter-permit-expired"
              />

              {params?.visaExpired == 'false' && (
                <MuiSelect
                  label={t('expires_in_months')}
                  emptyLabel={t('default')}
                  value={params?.expiredIn}
                  sort={false}
                  options={Array.from({ length: 24 }, (_, i) => i + 1)?.map((v) => {
                    return {
                      value: Number.parseInt(v),
                      label:
                        Number.parseInt(v) +
                        ` ${t('months')} ` +
                        `${Number.isInteger(Number.parseInt(v) / 12) ? '--' + Number.parseInt(v) / 12 + ` ${t('years')}` : ''}`,
                    }; // Traduit
                  })}
                  onChange={(e) => {
                    setParams({
                      ...params,
                      expiredIn: e.target.value?.toString(),
                    });
                  }}
                  wrapperClasses={'flex-1 filter-item anime slideRight'}
                />
              )}

              <MuiSelect
                label={t('source')}
                emptyLabel={t('all')}
                value={params?.fromArchive}
                options={[
                  { value: false, label: t('field') }, // Traduit
                  { value: true, label: t('archive') }, // Traduit
                ]}
                onChange={(e) => {
                  setParams({
                    ...params,
                    fromArchive: e.target.value !== '-1' ? e.target.value : '',
                  });
                }}
                wrapperClasses={'flex-1 filter-item anime slideRight'}
              />
              <MenuDrop menu={menu} />
            </Box>
          )}

        {creator && (
          <Box
            bg={'red.100'}
            boxShadow="lg"
            p={2}
            rounded={'md'}
            border="1px"
            borderColor="gray.200"
            className=" flex flex-wrap gap-1  items-end justify-center"
          >
            <span className="flex gap-2 ">
              <BsPersonFill size={20} className="text-blue-400" />
              {t('collected_by')} {/* Traduit */}
              <strong>
                {creator?.creator?.firstName} {creator?.lastName}
              </strong>
            </span>
          </Box>
        )}
        <Card extra={'w-full h-full pb-4'}>
          <div className="flex justify-between ">
            <div>
              <strong>
                {t('total')} : {/* Traduit */}
                <Badge
                  fontSize={13}
                  rounded={5}
                  padding={1}
                  paddingInline={2}
                  colorScheme="green"
                  className="m-0 ms-2"
                >
                  {formatNumber(employees?.totalElements) || '--'}
                </Badge>
              </strong>
            </div>
            <div className="flex flex-1 justify-center">
              <span style={{ marginTop: -15 }}>
                <ButtonGroup variant="text" aria-label="Basic button group">
                  <MButton
                    variant={params?.creatorId === auth()?.user?.id ? 'primary' : 'white'}
                    onClick={() =>
                      setParams({
                        ...params,
                        creatorId: params?.creatorId ? null : auth()?.user?.id,
                      })
                    }
                  >
                    <BsMinecart />
                    {t('my_work')}
                  </MButton>
                  <SearchModal
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    searchResults={searchEmployes?.data}
                    searchFunction={fetchDataSearch}
                    iconLabel={t('search')}
                    placeholder={t('search_placeholder')}
                    title={t('search_expatriate_title')}
                    handleClickResult={(item) => {
                      setSelectedEmployer(item);
                      setOpenAdd(true);
                    }}
                    loading={loadingSearch}
                  />
                  <MButton variant={'white'} onClick={() => setOpenEnterprisePicker(true)}>
                    <BsPlusCircle />
                    {t('add_expatriate')} {/* Traduit */}
                  </MButton>
                  <CheckAuthorization
                    roles={[
                      ROLES_LIST.ADMIN,
                      ROLES_LIST.AGENT,
                      ROLES_LIST.QUALITY,
                      ROLES_LIST.DRMO,
                    ]}
                  >
                    {exportPdf}
                  </CheckAuthorization>
                </ButtonGroup>
              </span>
            </div>
          </div>
          <MuiTable
            loading={loading}
            hidePagiation={true}
            columns={getEmployerHeaderWithHistory(t)} //TODO: Verifier
            rows={rows}
            textEmpty={noDataFoundText}
            dataCy="employes-table"
            withHistory={false}
          />{' '}
          {/* Traduit */}
          <PaginateBox
            total={employees?.totalElements}
            rowsPerPage={params?.limit}
            handleChangeRowsPerPage={(event) => {
              setParams({
                ...params,
                limit: parseInt(event.target.value),
                page: 0,
              });
            }}
            page={params.page}
            count={employees?.totalPages - 1}
            handleChange={(event, value) => {
              setParams({ ...params, page: value });
            }}
          />
        </Card>
      </div>
      <AddEmployer
        get={getEmployees}
        open={openAdd}
        handleClose={() => {
          setSelectedEmployer(null);
          setOpenAdd(false);
        }}
        selectedEmployerP={selectedEmployer}
      />
      <Dialog
        open={openEnterprisePicker}
        onClose={() => setOpenEnterprisePicker(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t('pick_enterprise_title')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>{t('pick_enterprise_help')}</DialogContentText>
          <MuiAsyncAutocomplete
            fullWidth
            label={t('pick_enterprise_label')}
            value={pickedEnterprise}
            format={{ value: 'id', label: 'socialRaison' }}
            fetch={'enterprises/search-all-by-term'}
            handleChange={(e, item) => setPickedEnterprise(item)}
          />
        </DialogContent>
        <DialogActions>
          <MButton variant={'white'} onClick={() => setOpenEnterprisePicker(false)}>
            {t('pick_enterprise_cancel')}
          </MButton>
          <MButton
            variant={'primary'}
            disabled={!pickedEnterprise}
            onClick={() => {
              setOpenEnterprisePicker(false);
              setOpenEmployesByEnterprise(true);
            }}
          >
            {t('pick_enterprise_confirm')}
          </MButton>
        </DialogActions>
      </Dialog>
      {openEmployesByEnterprise && pickedEnterprise && (
        <EmployesByEnterpriseModal
          open={openEmployesByEnterprise}
          setOpen={setOpenEmployesByEnterprise}
          Fullscreen={true}
          handleClose={() => {
            setOpenEmployesByEnterprise(false);
            setPickedEnterprise(null);
            getEmployees();
          }}
          selectedEnterprise={{
            id: pickedEnterprise?.value,
            socialRaison: pickedEnterprise?.label,
          }}
        />
      )}
    </div>
  );
};

export default EmployeList;
